import { useState, useEffect, useCallback } from "react";
import { Drawer, Box, CircularProgress, Typography, Alert } from "@mui/material";
import api from "../../services/api";
import KanbanDetails from "../KanbanDetails";

// Cache para armazenar dados de cards já carregados
const cardCache = new Map();

const KanbanCardDrawer = ({ open, onClose, cardId, assigneds, priorities, cardTags }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [card, setCard] = useState(null);

    // Função para buscar os dados do card com useCallback para memoização
    const fetchCardData = useCallback(async (id) => {
        // Verifica se os dados já estão em cache
        if (cardCache.has(id)) {
            setCard(cardCache.get(id));
            return;
        }

        setLoading(true);
        setError(null);

        // Cria um AbortController para cancelar a requisição se necessário
        const controller = new AbortController();
        const signal = controller.signal;

        try {
            const { data } = await api.get(`/kanban/card/${id}`, { signal });

            // Armazena os dados no cache
            cardCache.set(id, data);
            setCard(data);
        } catch (error) {
            // Ignora erros de requisições abortadas
            if (error.name !== 'AbortError') {
                console.error('Erro ao carregar o card:', error);
                setError('Não foi possível carregar os dados do card. Tente novamente.');
            }
        } finally {
            setLoading(false);
        }

        return () => controller.abort();
    }, []);
    useEffect(() => {
        let controller = null;

        if (open && cardId) {
            // Cria um AbortController aqui
            controller = new AbortController();

            const fetchData = async () => {
                // Verifica se os dados já estão em cache
                if (cardCache.has(cardId)) {
                    setCard(cardCache.get(cardId));
                    return;
                }

                setLoading(true);
                setError(null);

                try {
                    const { data } = await api.get(`/kanban/card/${cardId}`, {
                        signal: controller.signal
                    });

                    // Armazena os dados no cache
                    cardCache.set(cardId, data);
                    setCard(data);
                } catch (error) {
                    // Ignora erros de requisições abortadas
                    if (error.name !== 'AbortError') {
                        console.error('Erro ao carregar o card:', error);
                        setError('Não foi possível carregar os dados do card. Tente novamente.');
                    }
                } finally {
                    setLoading(false);
                }
            };

            // Chama a função interna
            fetchData();
        } else if (!open) {
            // Limpa os estados quando o drawer é fechado
            setCard(null);
            setError(null);
        }

        // Retorna uma função de limpeza adequada
        return () => {
            if (controller) {
                controller.abort();
            }
        };
    }, [open, cardId]);

    // Função para tentar novamente após um erro
    const handleRetry = () => {
        if (cardId) {
            fetchCardData(cardId);
        }
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={() => onClose(false)}
            PaperProps={{
                sx: {
                    width: 800,
                    p: 0,
                    backgroundColor: '#ffffff00',
                    backdropFilter: 'blur(20px)',
                },
            }}
        >
            {loading ? (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        height: '100%',
                        gap: 2,
                    }}
                >
                    <CircularProgress size={40} />
                    <Typography variant="body1" color="textSecondary">
                        Carregando dados...
                    </Typography>
                </Box>
            ) : error ? (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        height: '100%',
                        p: 3,
                    }}
                >
                    <Alert
                        severity="error"
                        sx={{ mb: 2, width: '100%' }}
                    >
                        {error}
                    </Alert>
                    <Typography
                        variant="body2"
                        sx={{
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            color: 'primary.main',
                            mt: 1,
                        }}
                        onClick={handleRetry}
                    >
                        Tentar novamente
                    </Typography>
                </Box>
            ) : card ? (
                <KanbanDetails card={card} priorities={priorities} cardTags={cardTags} assigneds={assigneds} />
            ) : (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                    }}
                >
                    <Typography variant="body1" color="textSecondary">
                        Nenhum card selecionado
                    </Typography>
                </Box>
            )}
        </Drawer>
    );
};

export default KanbanCardDrawer;